/**
 * Present-rate trend across sessions.
 *
 * One series, so there is no legend: the caption names what the line is. Sessions are spaced
 * evenly by position rather than by calendar distance, because the reader is comparing one
 * session to the next, not to a date axis. Sessions with nothing recorded are left out of the
 * line and counted in the caption instead of being drawn as zero.
 */

import { useMemo, useState, type PointerEvent, type ReactNode } from 'react';

import { formatRate, type SessionTally } from '../../../shared/stats.js';
import { formatShortDate } from '../format.js';
import { useMeasuredWidth } from './measure.js';

/** Axis gutters, in pixels. */
const PADDING = { top: 16, right: 16, bottom: 30, left: 46 } as const;

/** Gridline positions on the rate axis. */
const GRID_RATES = [0, 0.25, 0.5, 0.75, 1] as const;

/** Radius of the marker drawn on the hovered session. */
const MARKER_RADIUS = 5;

/** Smallest gap allowed between two date labels, in pixels, at a 13px type size. */
const MIN_LABEL_GAP_PX = 58;

/** Date labels wanted on a narrow viewport, and on a wide one. */
const LABEL_TARGET = { narrow: 4, wide: 8 } as const;

/** Viewport width below which the axis uses the narrow label budget. */
const NARROW_WIDTH_PX = 480;

/** A session that has a rate, paired with its drawn position. */
interface Plotted {
  tally: SessionTally;
  x: number;
  y: number;
  rate: number;
}

/** Renders the line chart of present rate over sessions. */
export function RateLine({
  tallies,
  height = 220,
}: {
  /** Sessions in ascending date order. */
  tallies: readonly SessionTally[];
  height?: number;
}): ReactNode {
  const [container, width] = useMeasuredWidth<HTMLDivElement>();
  const [hovered, setHovered] = useState<number | null>(null);

  const plotWidth = Math.max(1, width - PADDING.left - PADDING.right);
  const plotHeight = Math.max(1, height - PADDING.top - PADDING.bottom);

  const points = useMemo((): Plotted[] => {
    const recorded = tallies.filter((tally) => tally.rate !== null);
    const step = recorded.length <= 1 ? 0 : plotWidth / (recorded.length - 1);
    return recorded.map((tally, index) => {
      const rate = tally.rate ?? 0;
      return {
        tally,
        rate,
        x: PADDING.left + (recorded.length === 1 ? plotWidth / 2 : index * step),
        y: PADDING.top + (1 - rate) * plotHeight,
      };
    });
  }, [tallies, plotWidth, plotHeight]);

  const path = useMemo(
    () => points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' '),
    [points],
  );

  // The same line closed down to the zero baseline. A rate is a share of a whole, and the
  // filled area is what says so — without it the axis below 50% reads as wasted space rather
  // than as the part of the roster that was not present.
  const area = useMemo(() => {
    const first = points[0];
    const last = points.at(-1);
    if (first === undefined || last === undefined) return '';
    const baseline = (PADDING.top + plotHeight).toFixed(1);
    return `M${first.x.toFixed(1)} ${baseline} ${path.slice(1)} L${last.x.toFixed(1)} ${baseline} Z`;
  }, [points, path, plotHeight]);

  /** Highlights the session nearest the pointer. */
  const trackPointer = (event: PointerEvent<SVGSVGElement>): void => {
    if (points.length === 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    let nearest = 0;
    for (let index = 1; index < points.length; index += 1) {
      if (Math.abs(points[index].x - x) < Math.abs(points[nearest].x - x)) nearest = index;
    }
    setHovered(nearest);
  };

  const active = hovered === null ? null : points[hovered] ?? null;
  const last = points.at(-1) ?? null;

  // The final session is always labelled because it is the one readers look for; earlier
  // labels are spaced evenly and then dropped where they would crowd it.
  const labelled = useMemo((): Set<number> => {
    if (points.length === 0) return new Set();
    const lastIndex = points.length - 1;
    const budget = width < NARROW_WIDTH_PX ? LABEL_TARGET.narrow : LABEL_TARGET.wide;
    const step = Math.max(1, Math.ceil(points.length / budget));
    const chosen = new Set<number>([lastIndex]);
    for (let index = 0; index < lastIndex; index += step) {
      if (points[lastIndex].x - points[index].x >= MIN_LABEL_GAP_PX) chosen.add(index);
    }
    return chosen;
  }, [points, width]);

  return (
    <div ref={container} className="w-full">
      {points.length === 0 ? (
        <p className="py-8 text-ink-2">No attendance recorded yet, so there is no trend to draw.</p>
      ) : (
        <div className="relative">
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={`Present rate across ${points.length} recorded sessions.`}
            onPointerMove={trackPointer}
            onPointerLeave={() => setHovered(null)}
            className="touch-pan-y"
          >
            <path d={area} fill="var(--color-brand-graphic)" opacity={0.12} />

            {GRID_RATES.map((rate) => {
              const y = PADDING.top + (1 - rate) * plotHeight;
              return (
                <g key={rate}>
                  <line
                    x1={PADDING.left}
                    x2={width - PADDING.right}
                    y1={y}
                    y2={y}
                    stroke="var(--color-line)"
                    strokeWidth={1}
                  />
                  <text
                    x={PADDING.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fontSize={13}
                    fill="var(--color-ink-3)"
                    className="tabular"
                  >
                    {/* Only the top tick carries the unit; repeating it on every tick is noise. */}
                    {rate === 1 ? '100%' : Math.round(rate * 100)}
                  </text>
                </g>
              );
            })}

            {points.map((point, index) => (
              labelled.has(index) ? (
                <text
                  key={point.tally.date}
                  x={point.x}
                  y={height - 10}
                  textAnchor={index === points.length - 1 ? 'end' : index === 0 ? 'start' : 'middle'}
                  fontSize={13}
                  fill="var(--color-ink-3)"
                >
                  {formatShortDate(point.tally.date)}
                </text>
              ) : null
            ))}

            <path d={path} fill="none" stroke="var(--color-brand-graphic)" strokeWidth={2} strokeLinejoin="round" />

            {last === null ? null : (
              <g>
                <circle cx={last.x} cy={last.y} r={MARKER_RADIUS} fill="var(--color-brand-graphic)"
                  stroke="var(--color-surface)" strokeWidth={2} />
              </g>
            )}

            {active === null ? null : (
              <g>
                <line
                  x1={active.x}
                  x2={active.x}
                  y1={PADDING.top}
                  y2={PADDING.top + plotHeight}
                  stroke="var(--color-line-strong)"
                  strokeWidth={1}
                />
                <circle
                  cx={active.x}
                  cy={active.y}
                  r={MARKER_RADIUS + 1}
                  fill="var(--color-accent)"
                  stroke="var(--color-surface)"
                  strokeWidth={2}
                />
              </g>
            )}
          </svg>

          {active === null ? null : (
            <div
              className="pointer-events-none absolute top-0 rounded-control border border-line
                bg-surface px-3 py-2 text-tiny shadow-sm"
              style={{
                left: Math.min(Math.max(active.x - 70, 0), Math.max(0, width - 150)),
              }}
            >
              <p className="font-semibold text-ink">{formatShortDate(active.tally.date)}</p>
              <p className="tabular text-ink-2">
                {formatRate(active.rate)} present · {active.tally.counts.attending} attending
                {active.tally.counts.excused > 0 ? `, ${active.tally.counts.excused} excused` : ''}
                {active.tally.counts.missing > 0 ? `, ${active.tally.counts.missing} missing` : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
